import { useQuery } from '@tanstack/react-query';

import type { MultichainPortfolioConfig } from 'types/client/multichainPortfolioConfig';

import config from 'configs/app';

import { DEFAULT_MULTICHAIN_CHAINS_CONFIG } from './defaultChainsConfig';

const ALLOWED_CHAIN_IDS = new Set([
  'eth', 'bsc', 'polygon', 'avalanche', 'arbitrum', 'optimism', 'base',
  'linea', 'gnosis', 'cronos', 'fantom', 'chiliz', 'pulse', 'moonbeam',
  'moonriver', 'flow', 'ronin', 'lisk', 'sei',
]);

function validateConfig(data: unknown): MultichainPortfolioConfig {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid multichain portfolio config');
  }

  const configData = data as MultichainPortfolioConfig;

  if (!Array.isArray(configData.chains) || configData.chains.length === 0) {
    throw new Error('Invalid multichain portfolio config: empty chains');
  }

  if (configData.chains.length > 25) {
    throw new Error('Invalid multichain portfolio config: too many chains');
  }

  configData.chains.forEach((chain) => {
    if (!chain.id || !ALLOWED_CHAIN_IDS.has(chain.id)) {
      throw new Error(`Invalid chain id: ${ chain.id }`);
    }
    if (!chain.explorer_label) {
      throw new Error(`Missing explorer_label for chain: ${ chain.id }`);
    }
  });

  return configData;
}

type Options = {
  enabled?: boolean;
};

export default function useMultichainChainsConfig(options?: Options) {
  const feature = config.features.multichainPortfolio;
  const url = feature.isEnabled ? feature.chainsConfigUrl : undefined;

  return useQuery({
    queryKey: [ 'multichain-chains-config', url ],
    enabled: feature.isEnabled && (options?.enabled ?? true),
    staleTime: 3_600_000,
    retry: false,
    queryFn: async() => {
      if (!url) {
        return validateConfig(DEFAULT_MULTICHAIN_CHAINS_CONFIG);
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch multichain chains config');
      }

      const json = await response.json();
      return validateConfig(json);
    },
  });
}
